import { motion, useReducedMotion } from 'motion/react'
import { type ReactNode, useEffect, useId, useState } from 'react'
import { useInView } from 'react-intersection-observer'

const LOOP_RESET_DELAY_MS = 6500
const LOOP_RESTART_DELAY_MS = 7000
const motionEase = [0.22, 1, 0.36, 1] as const

const recipients = [
	{
		email: 'm•••@example.com',
		initials: 'MP',
		name: 'Maya Patel',
		position: 'top-[20%] -translate-y-1/2',
	},
	{
		email: 'j•••@example.com',
		initials: 'JL',
		name: 'Jordan Lee',
		position: 'top-1/2 -translate-y-1/2',
	},
	{
		email: 'a•••@example.com',
		initials: 'AR',
		name: 'Alex Rivera',
		position: 'top-[80%] -translate-y-1/2',
	},
]

const connectors = [
	{ delay: 1.62, id: 'trunk', path: 'M 78 170 L 108 170' },
	{
		delay: 1.98,
		id: 'maya',
		path: 'M 108 170 C 112 145, 110 68, 132 68',
	},
	{ delay: 2.28, id: 'jordan', path: 'M 108 170 L 132 170' },
	{
		delay: 2.58,
		id: 'alex',
		path: 'M 108 170 C 112 195, 110 272, 132 272',
	},
]

type AnimationPhase = 'idle' | 'playing' | 'reduced' | 'resetting'

function getAnimationDelay(phase: AnimationPhase, delay: number) {
	return phase === 'playing' ? delay : 0
}

function getAnimationDuration(
	phase: AnimationPhase,
	playingDuration: number,
	resettingDuration = 0.3,
) {
	if (phase === 'playing') return playingDuration
	if (phase === 'resetting') return resettingDuration
	return 0
}

export function PriceDropAlertIllustration() {
	const shouldReduceMotion = Boolean(useReducedMotion())
	const { inView, ref } = useInView({ threshold: 0.35 })
	const maskId = useId().replaceAll(':', '')
	const [cycle, setCycle] = useState(0)
	const [isResetting, setIsResetting] = useState(false)

	useEffect(() => {
		if (!inView || shouldReduceMotion) return

		setIsResetting(false)

		const resetTimer = window.setTimeout(() => {
			setIsResetting(true)
		}, LOOP_RESET_DELAY_MS)
		const restartTimer = window.setTimeout(() => {
			setCycle((currentCycle) => currentCycle + 1)
		}, LOOP_RESTART_DELAY_MS)

		return () => {
			window.clearTimeout(resetTimer)
			window.clearTimeout(restartTimer)
		}
	}, [cycle, inView, shouldReduceMotion])

	const phase: AnimationPhase = shouldReduceMotion
		? 'reduced'
		: !inView
			? 'idle'
			: isResetting
				? 'resetting'
				: 'playing'
	const isPlaying = phase === 'playing'
	const showCompletedState = phase === 'playing' || phase === 'reduced'

	return (
		<div
			aria-label="A $981 price drop on a 2019 Ford EcoSport sends email alerts to three shoppers."
			className="relative min-h-[660px] w-full text-[#182238] sm:aspect-[16/10] sm:min-h-0"
			ref={ref}
			role="img"
		>
			<div
				aria-hidden="true"
				className="relative grid h-full grid-cols-1 sm:grid-cols-[1.08fr_0.92fr]"
			>
				<div className="relative flex min-h-[390px] items-center px-4 py-6 sm:min-h-0 sm:px-[clamp(1rem,2.4vw,1.75rem)] sm:py-5">
					<div className="mx-auto w-full max-w-[390px]">
						<motion.div
							animate={
								showCompletedState
									? {
											clipPath: 'inset(0 0 0% 0 round 11px)',
											opacity: 1,
											scale: 1,
											y: 0,
										}
									: {
											clipPath: 'inset(0 0 42% 0 round 11px)',
											opacity: 0,
											scale: 0.96,
											y: 44,
										}
							}
							className="relative z-0 mx-auto w-[86%] overflow-hidden rounded-t-[11px] bg-[#ee0009] px-4 pb-9 pt-3 text-white shadow-[0_14px_30px_-22px_rgba(238,0,9,0.9)]"
							initial={
								shouldReduceMotion
									? false
									: {
											clipPath: 'inset(0 0 42% 0 round 11px)',
											opacity: 0,
											scale: 0.96,
											y: 44,
										}
							}
							transition={{
								delay: getAnimationDelay(phase, 0.32),
								duration: getAnimationDuration(phase, 0.72, 0.38),
								ease: motionEase,
							}}
						>
							<div className="flex items-start gap-3">
								<PayloadPriceDropMark />
								<div className="min-w-0 pt-0.5">
									<p className="text-[10px] font-extrabold uppercase leading-none tracking-[0.02em] sm:text-[11px]">
										Recent Price Drop!
									</p>
									<motion.p
										animate={
											showCompletedState
												? { opacity: 1, y: 0 }
												: { opacity: 0, y: -5 }
										}
										className="mt-2 whitespace-nowrap text-[9px] font-medium leading-none sm:text-[10px]"
										initial={shouldReduceMotion ? false : { opacity: 0, y: -5 }}
										transition={{
											delay: getAnimationDelay(phase, 0.9),
											duration: getAnimationDuration(phase, 0.42),
											ease: motionEase,
										}}
									>
										Reduced by $981 since Feb 6, 2026
									</motion.p>
								</div>
							</div>
						</motion.div>

						<div className="relative z-10 -mt-5 overflow-hidden border border-[#d9dde5] bg-white shadow-[0_12px_30px_-20px_rgba(24,34,56,0.45)]">
							<div className="flex items-start justify-between gap-3 border-b border-[#e1e4ea] px-4 py-4 sm:px-5">
								<div>
									<p className="text-[clamp(1rem,2.5vw,1.45rem)] font-extrabold leading-none tracking-[-0.02em]">
										2019 Ford EcoSport
									</p>
									<p className="mt-2 text-[clamp(0.82rem,1.8vw,1rem)] font-bold leading-none">
										Titanium
									</p>
								</div>
								<div className="flex shrink-0 flex-col gap-1.5">
									<CircleIcon label="Share">
										<ShareIcon />
									</CircleIcon>
									<CircleIcon label="Favorite">
										<HeartIcon />
									</CircleIcon>
								</div>
							</div>

							<div className="px-4 py-5 text-center sm:px-5 sm:py-6">
								<p className="text-[clamp(2rem,5vw,3rem)] font-extrabold leading-none tracking-[-0.03em] text-[#1d365c]">
									$9,500
								</p>
								<p className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#586174]">
									Our Price
								</p>
								<div className="mx-auto mt-4 h-px w-full bg-[#e1e4ea]" />
							</div>

							<div className="space-y-2 bg-[#fbfbfc] px-4 py-4 sm:px-5">
								<div className="bg-black px-3 py-2.5 text-center text-[10px] font-bold text-white sm:text-xs">
									Get Pre-Approved
								</div>
								<div className="bg-black px-3 py-2.5 text-center text-[10px] font-bold text-white sm:text-xs">
									Check Availability
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="relative min-h-[270px] overflow-hidden sm:min-h-0">
					<div className="absolute left-4 top-4 z-20 flex items-center gap-2 sm:left-5 sm:top-5">
						<span className="h-2 w-2 bg-[#ee0009]" />
						<p className="text-[9px] font-extrabold uppercase tracking-[0.16em] text-[#4d576a] sm:text-[10px]">
							Automated email alert
						</p>
					</div>

					<svg
						className="absolute inset-0 h-full w-full"
						preserveAspectRatio="none"
						viewBox="0 0 360 340"
					>
						<defs>
							{connectors.map((connector) => (
								<mask
									height="340"
									id={`${maskId}-${connector.id}`}
									key={connector.id}
									maskUnits="userSpaceOnUse"
									width="360"
									x="0"
									y="0"
								>
									<motion.path
										animate={{
											opacity: showCompletedState ? 1 : 0,
											pathLength: showCompletedState ? 1 : 0,
										}}
										d={connector.path}
										fill="none"
										initial={
											shouldReduceMotion ? false : { opacity: 0, pathLength: 0 }
										}
										stroke="white"
										strokeLinecap="round"
										strokeWidth="10"
										transition={{
											delay: getAnimationDelay(phase, connector.delay),
											duration: getAnimationDuration(
												phase,
												connector.id === 'trunk' ? 0.38 : 0.58,
												0.26,
											),
											ease: motionEase,
										}}
									/>
								</mask>
							))}
						</defs>

						{connectors.map((connector) => (
							<path
								d={connector.path}
								fill="none"
								key={connector.id}
								mask={`url(#${maskId}-${connector.id})`}
								stroke="#9aa3b2"
								strokeDasharray="1 10"
								strokeLinecap="round"
								strokeWidth="3"
							/>
						))}

						{recipients.map((recipient, index) => {
							const endY = 68 + index * 102

							return (
								<motion.circle
									animate={
										shouldReduceMotion
											? { cx: 132, cy: endY, opacity: 0 }
											: isPlaying
												? {
														cx: [82, 108, 132],
														cy: [170, 170, endY],
														opacity: [0, 1, 0],
													}
												: { cx: 82, cy: 170, opacity: 0 }
									}
									fill="#ee0009"
									initial={{ cx: 82, cy: 170, opacity: 0 }}
									key={`pulse-${recipient.email}`}
									r="4"
									transition={{
										delay: getAnimationDelay(phase, 2.36 + index * 0.32),
										duration: getAnimationDuration(phase, 0.82, 0.2),
										ease: 'easeInOut',
									}}
								/>
							)
						})}
					</svg>

					<div className="absolute left-[6%] top-1/2 z-10 -translate-y-1/2">
						<motion.div
							animate={
								showCompletedState
									? { opacity: 1, scale: 1 }
									: { opacity: 0, scale: 0.72 }
							}
							className="relative flex h-14 w-14 items-center justify-center rounded-full border border-[#ee0009]/35 bg-white shadow-[0_10px_30px_-16px_rgba(238,0,9,0.8)]"
							initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.72 }}
							transition={{
								delay: getAnimationDelay(phase, 1.28),
								duration: getAnimationDuration(phase, 0.55),
								ease: motionEase,
							}}
						>
							<motion.span
								animate={
									shouldReduceMotion
										? { opacity: 0, scale: 1 }
										: isPlaying
											? {
													opacity: [0, 0.45, 0],
													scale: [1, 1.72, 1.92],
												}
											: { opacity: 0, scale: 1 }
								}
								className="absolute inset-0 rounded-full border border-[#ee0009]"
								transition={{
									delay: getAnimationDelay(phase, 1.45),
									duration: getAnimationDuration(phase, 1.05),
									ease: 'easeOut',
								}}
							/>
							<MailIcon />
						</motion.div>
					</div>

					<div className="absolute inset-y-0 left-[32%] right-[3%]">
						{recipients.map((recipient, index) => (
							<div
								className={`absolute left-0 w-full ${recipient.position}`}
								key={recipient.email}
							>
								<motion.div
									animate={
										showCompletedState
											? { opacity: 1, scale: 1, x: 0 }
											: { opacity: 0.48, scale: 0.98, x: 10 }
									}
									className="flex w-full items-center gap-2.5 border border-[#d8dce4] bg-white px-2.5 py-2 shadow-[0_8px_24px_-18px_rgba(24,34,56,0.55)]"
									initial={
										shouldReduceMotion
											? false
											: { opacity: 0.48, scale: 0.98, x: 10 }
									}
									transition={{
										delay: getAnimationDelay(phase, 2.6 + index * 0.32),
										duration: getAnimationDuration(phase, 0.48, 0.28),
										ease: motionEase,
									}}
								>
									<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#edf0f5] text-[9px] font-extrabold text-[#27344b]">
										{recipient.initials}
									</div>
									<div className="min-w-0 flex-1">
										<p className="truncate text-[10px] font-extrabold leading-tight text-[#202a3d]">
											{recipient.name}
										</p>
										<p className="mt-0.5 truncate text-[8px] leading-tight text-[#727b8c] sm:text-[9px]">
											{recipient.email}
										</p>
									</div>
									<motion.div
										animate={
											showCompletedState
												? { opacity: 1, scale: 1 }
												: { opacity: 0, scale: 0.78 }
										}
										className="flex shrink-0 items-center gap-1 bg-[#e9f7ef] px-1.5 py-1 text-[7px] font-extrabold uppercase tracking-[0.04em] text-[#18794e]"
										initial={
											shouldReduceMotion ? false : { opacity: 0, scale: 0.78 }
										}
										transition={{
											delay: getAnimationDelay(phase, 2.92 + index * 0.32),
											duration: getAnimationDuration(phase, 0.35),
											ease: motionEase,
										}}
									>
										<CheckIcon />
										<span>Delivered</span>
									</motion.div>
								</motion.div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

function CircleIcon({
	children,
	label,
}: {
	children: ReactNode
	label: string
}) {
	return (
		<div
			aria-label={label}
			className="flex h-7 w-7 items-center justify-center rounded-full border border-[#cfd4dd] text-[#536075] sm:h-8 sm:w-8"
		>
			{children}
		</div>
	)
}

function PayloadPriceDropMark() {
	return (
		<div className="relative -ml-4 -mt-3 h-10 w-10 shrink-0 sm:h-11 sm:w-11">
			<PayloadFlagBannerIcon />
			<div className="absolute inset-0 -ml-[3px] -mt-2 flex items-center justify-center -space-x-0.5">
				<PayloadPriceDropIcon />
				<PayloadDropArrowIcon />
			</div>
		</div>
	)
}

function PayloadFlagBannerIcon() {
	return (
		<svg
			className="-rotate-90"
			fill="none"
			height="40"
			viewBox="0 0 24 24"
			width="40"
			xmlns="http://www.w3.org/2000/svg"
		>
			<desc>Flag Shape Streamline Icon: https://streamlinehq.com</desc>
			<path
				clipRule="evenodd"
				d="M1 3.75a0.75 0.75 0 0 0 -0.48 1.326L8.829 12l-8.31 6.924A0.75 0.75 0 0 0 1 20.25h22a0.75 0.75 0 0 0 0.75 -0.75v-15a0.75 0.75 0 0 0 -0.75 -0.75H1Z"
				fill="white"
				fillRule="evenodd"
				stroke="#dc2626"
				strokeWidth="1"
			/>
		</svg>
	)
}

function PayloadPriceDropIcon() {
	return (
		<svg
			height="16"
			id="Dollar-Sign--Streamline-Font-Awesome"
			viewBox="0 0 16 16"
			width="16"
			xmlns="http://www.w3.org/2000/svg"
		>
			<desc>Dollar Sign Streamline Icon: https://streamlinehq.com</desc>
			<path
				d="M8.024515 0.16000000000000003c0.5420600000000001 0 0.9800000000000001 0.43793499999999996 0.9800000000000001 0.9800000000000001v1.09331c0.049 0.006125 0.094935 0.01225 0.143935 0.02144 0.01225 0.003065 0.02144 0.003065 0.03369 0.006125l1.47 0.2695c0.5328750000000001 0.098 0.8850600000000001 0.6094350000000001 0.78706 1.13925s-0.6094350000000001 0.885065 -1.13925 0.78706l-1.454685 -0.26643500000000003c-0.958565 -0.140875 -1.8038150000000002 -0.04594 -2.3979399999999997 0.18987500000000002s-0.8330000000000001 0.560435 -0.888125 0.8605650000000001c-0.061250000000000006 0.327685 -0.015310000000000002 0.511435 0.03675 0.62475 0.05512500000000001 0.11943500000000001 0.16844 0.25418500000000005 0.392 0.40425000000000005 0.49919 0.327685 1.264815 0.5420600000000001 2.2570650000000003 0.805435l0.08881 0.0245c0.875875 0.23275 1.94775 0.5145 2.744 1.035125 0.434875 0.28481 0.8452500000000001 0.6706850000000001 1.0994400000000002 1.2096850000000001 0.26031 0.5481900000000001 0.315435 1.16069 0.196 1.8130050000000002 -0.21131500000000003 1.16375 -1.0136900000000002 1.9416250000000002 -2.009 2.3489350000000004 -0.41956499999999997 0.1715 -0.875875 0.28175 -1.35975 0.33687500000000004V14.86c0 0.542065 -0.43794000000000005 0.9800000000000001 -0.9800000000000001 0.9800000000000001s-0.9800000000000001 -0.43793499999999996 -0.9800000000000001 -0.9800000000000001v-1.068815c-0.01225 -0.0030600000000000002 -0.027565000000000003 -0.0030600000000000002 -0.039815 -0.006125h-0.006125c-0.7472500000000001 -0.116375 -1.9753100000000001 -0.43793499999999996 -2.802185 -0.805435 -0.49306500000000003 -0.22050000000000003 -0.7166250000000001 -0.799315 -0.496125 -1.292375s0.7993100000000001 -0.7166250000000001 1.292375 -0.496125c0.6400600000000001 0.284815 1.6935600000000002 0.5665650000000001 2.3030000000000004 0.6615000000000001 0.976935 0.14394 1.782375 0.061250000000000006 2.3275 -0.16231 0.51756 -0.21131500000000003 0.7533750000000001 -0.517565 0.82075 -0.885065 0.058185 -0.324625 0.01225 -0.51144 -0.039815 -0.62475 -0.058185 -0.12250000000000001 -0.1715 -0.25725 -0.39812500000000006 -0.40731500000000004 -0.50225 -0.327685 -1.2709350000000001 -0.5420600000000001 -2.2662500000000003 -0.805435l-0.08575 -0.02144c-0.87281 -0.23275 -1.9446850000000002 -0.51756 -2.7409350000000003 -1.0381850000000001 -0.434875 -0.284815 -0.8421900000000001 -0.6737500000000001 -1.0963749999999999 -1.21275 -0.25725 -0.5481900000000001 -0.309315 -1.16069 -0.186815 -1.813C3.850325 3.7125000000000004 4.7262 2.9530000000000003 5.721515 2.5579350000000005c0.40731000000000006 -0.16231 0.8544350000000001 -0.27256 1.3230000000000002 -0.33687500000000004V1.1400000000000001c0 -0.542065 0.43793499999999996 -0.9800000000000001 0.9800000000000001 -0.9800000000000001Z"
				fill="#dc2626"
				strokeWidth="0.05"
			/>
		</svg>
	)
}

function PayloadDropArrowIcon() {
	return (
		<svg
			className="-translate-x-px"
			fill="none"
			height="14"
			viewBox="0 0 24 24"
			width="14"
			xmlns="http://www.w3.org/2000/svg"
		>
			<desc>Arrow Down Streamline Icon: https://streamlinehq.com</desc>
			<path
				d="M23.1807 10.6094c-0.1821 -0.1835 -0.3987 -0.3292 -0.6372 -0.4287 -0.2388 -0.0995 -0.4947 -0.1506 -0.7532 -0.1506s-0.5145 0.0511 -0.7532 0.1506c-0.2386 0.0995 -0.4552 0.2452 -0.6373 0.4287l-6.4431 6.4626V2.2079c0 -0.5194 -0.2064 -1.0175 -0.5736 -1.3848 -0.3672 -0.3673 -0.8654 -0.5736 -1.3848 -0.5736 -0.5193 0 -1.0176 0.2063 -1.3847 0.5736 -0.3672 0.3673 -0.5737 0.8654 -0.5737 1.3848V17.072l-6.443 -6.4626c-0.3688 -0.3688 -0.869 -0.576 -1.3905 -0.576s-1.0217 0.2072 -1.3904 0.576c-0.3688 0.3687 -0.576 0.8689 -0.576 1.3904s0.2072 1.0217 0.576 1.3905l9.7919 9.7919c0.1862 0.1782 0.406 0.318 0.6463 0.4112 0.2344 0.1036 0.4878 0.1571 0.7441 0.1571 0.2564 0 0.5098 -0.0535 0.7442 -0.1571 0.2405 -0.0932 0.4601 -0.233 0.6463 -0.4112l9.7919 -9.7919c0.1835 -0.1822 0.3292 -0.3988 0.4287 -0.6373 0.0995 -0.2387 0.1506 -0.4947 0.1506 -0.7532s-0.0511 -0.5145 -0.1506 -0.7532c-0.0995 -0.2385 -0.2452 -0.4551 -0.4287 -0.6373Z"
				fill="#dc2626"
				strokeWidth="1"
			/>
		</svg>
	)
}

function ShareIcon() {
	return (
		<svg fill="none" height="14" viewBox="0 0 24 24" width="14">
			<circle cx="18" cy="5" r="2.5" stroke="currentColor" strokeWidth="2" />
			<circle cx="6" cy="12" r="2.5" stroke="currentColor" strokeWidth="2" />
			<circle cx="18" cy="19" r="2.5" stroke="currentColor" strokeWidth="2" />
			<path
				d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5"
				stroke="currentColor"
				strokeWidth="2"
			/>
		</svg>
	)
}

function HeartIcon() {
	return (
		<svg fill="none" height="15" viewBox="0 0 24 24" width="15">
			<path
				d="M20.8 5.9c-1.7-1.7-4.5-1.7-6.2 0L12 8.5 9.4 5.9a4.38 4.38 0 0 0-6.2 6.2L12 21l8.8-8.9c1.7-1.7 1.7-4.5 0-6.2Z"
				stroke="currentColor"
				strokeLinejoin="round"
				strokeWidth="2"
			/>
		</svg>
	)
}

function MailIcon() {
	return (
		<svg fill="none" height="22" viewBox="0 0 24 24" width="22">
			<rect
				height="15"
				rx="2"
				stroke="#ee0009"
				strokeWidth="1.8"
				width="19"
				x="2.5"
				y="4.5"
			/>
			<path
				d="m4.5 7 7.5 6 7.5-6"
				stroke="#ee0009"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.8"
			/>
		</svg>
	)
}

function CheckIcon() {
	return (
		<svg fill="none" height="8" viewBox="0 0 10 10" width="8">
			<path
				d="m1.5 5.2 2.2 2.2 4.8-5"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.7"
			/>
		</svg>
	)
}
